/**
 * Account User Fragment - ES6 Module
 * Handles user-accounts list selection and bulk-toolbar state.
 */
import { Events } from '../../../../js/utils/events.js';

let initialized = false;

const AccountUserHandlers = {
    /**
     * Handle "select all" checkbox change.
     */
    onSelectAll(e, element) {
        const container = element.closest('#user-accounts');
        if (!container) return;

        const rowCheckboxes = container.querySelectorAll('[data-user-row-checkbox]');
        rowCheckboxes.forEach(cb => {
            cb.checked = element.checked;
        });

        // Programmatic .checked does not fire `change`, so HTMX's
        // hx-trigger="change from:[data-user-row-checkbox]" wouldn't
        // see the bulk flip. Dispatch one bubbling change event; the
        // form's delay:50ms collapses repeats so one is enough.
        if (rowCheckboxes.length > 0) {
            rowCheckboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
    },

    /**
     * Handle individual row checkbox change.
     */
    onRowCheckboxChange(e, element) {
        const container = element.closest('#user-accounts');
        if (!container) return;
        AccountUserHandlers.updateSelectAllState(container);
    },

    /**
     * Update "select all" checkbox state based on row selections.
     */
    updateSelectAllState(container) {
        const selectAllCheckbox = container.querySelector('[data-user-select-all]');
        if (!selectAllCheckbox) return;

        const rowCheckboxes = container.querySelectorAll('[data-user-row-checkbox]');
        const checkedCount = container.querySelectorAll('[data-user-row-checkbox]:checked').length;

        if (checkedCount === 0) {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = false;
        } else if (checkedCount === rowCheckboxes.length) {
            selectAllCheckbox.checked = true;
            selectAllCheckbox.indeterminate = false;
        } else {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = true;
        }
    },
};

/**
 * Initialize account-user fragment.
 * Called from main.js on DOMContentLoaded.
 */

// ============================================================================
// Add To Groups Modal
// Closes on Cancel, on Esc, on a click on the backdrop itself, and once the
// bulk request comes back. Blocks the request when no group was named.
// Template contract:
//   <div data-groups-modal>
//     <form data-groups-form>
//       <input name="groups" data-groups-input>
//       <button data-groups-close>
// ============================================================================

const AddGroupsModal = {
    close() {
        const modal = document.querySelector('[data-groups-modal]');
        if (modal) modal.remove();
    },

    onBackdropClick(e, element) {
        if (e.target === element) AddGroupsModal.close();
    },

    onKeyDown(e) {
        if (e.key === 'Escape') AddGroupsModal.close();
    },
};

export function initAccountUser() {
    if (initialized) return;
    initialized = true;

    Events.on('change', 'user-select-all', AccountUserHandlers.onSelectAll);
    Events.on('change', 'user-row-checkbox', AccountUserHandlers.onRowCheckboxChange);

    Events.on('click', 'groups-close', AddGroupsModal.close);
    Events.on('click', 'groups-modal', AddGroupsModal.onBackdropClick);

    // One listener for the life of the page. The old script registered a fresh
    // one every time the modal rendered and never removed it.
    document.addEventListener('keydown', AddGroupsModal.onKeyDown);

    document.addEventListener('htmx:beforeRequest', (event) => {
        const form = event.target.closest && event.target.closest('[data-groups-form]');
        if (!form) return;
        const groups = form.querySelector('[data-groups-input]');
        if (groups && !groups.value.trim()) {
            event.preventDefault();
            alert('Please specify at least one group to add the users.');
            groups.focus();
        }
    });

    document.addEventListener('htmx:afterRequest', (event) => {
        if (event.target.closest && event.target.closest('[data-groups-form]')) {
            AddGroupsModal.close();
        }
    });
}
